import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Typography, Drawer, makeStyles } from '@material-ui/core';
import ActionButton from '../actionButton';
import Scrollbars from '../scrollbars';
import { ACTION_BUTTON_TYPES } from '../actionButton/config';

const useStyles = makeStyles(
  ({ palette: { primary }, spacing, breakpoints }) => ({
    wholeScreen: {
      width: '100vw',
    },
    fullWidth: {
      width: 'calc(100vw - 200px)',
      [breakpoints.up('xl')]: {
        width: 'calc(100vw - 245px)',
      },
    },
    middleWidth: {
      width: 940,
    },
    largeWidth: {
      width: 600,
    },
    smallWidth: {
      width: 'auto',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      maxHeight: 71,
      padding: spacing(5, 6, 5, 8),
      borderBottom: `1px solid ${primary.bluish5}`,
    },
    headerFullWidth: {
      padding: spacing(5, 8),
    },
    closeButton: {
      display: 'flex',
      marginLeft: 'auto',
    },
    scrollWrapper: {
      boxSizing: 'border-box',
      padding: spacing(6, 8),
      width: '100%',
      minHeight: 0,
    },
    scrollWrapperNoPadding: {
      padding: 0,
    },
    content: {
      boxSizing: 'border-box',
      display: 'flex',
      width: '100%',
      minHeight: 0,
    },
    scrollY: {
      right: -19,
    },
    staticContent: {
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      height: '100%',
      minHeight: 0,
    },
  })
);

const CustomDrawer = ({
  paperClass = undefined,
  scrollWrapperClass = undefined,
  contentClass = undefined,
  title,
  isOpened,
  children,
  onClose,
  isWholeScreen = false,
  isFullWidth = false,
  isMiddleWidth = false,
  isLarge = false,
  isSmallWidth = false,
  disableScroll = false,
}) => {
  const classes = useStyles();
  const hasExplicitWidth =
    isWholeScreen || isFullWidth || isMiddleWidth || isLarge || isSmallWidth;
  const isWideDrawer = isFullWidth || isWholeScreen || !hasExplicitWidth;
  const useFullWidthHeader = isFullWidth || isWholeScreen;

  return (
    <Drawer
      anchor="right"
      open={isOpened}
      classes={{
        paper: classNames(
          {
            [classes.wholeScreen]: isWholeScreen,
            [classes.fullWidth]: isWideDrawer,
            [classes.middleWidth]: isMiddleWidth,
            [classes.largeWidth]: isLarge,
            [classes.smallWidth]: isSmallWidth,
          },
          paperClass
        ),
      }}
      onClose={onClose}
    >
      <div
        className={classNames(classes.header, {
          [classes.headerFullWidth]: useFullWidthHeader,
        })}
      >
        <Typography variant="h4">{title}</Typography>
        <ActionButton
          className={classes.closeButton}
          type={ACTION_BUTTON_TYPES.CLOSE}
          onClickHandler={onClose}
        />
      </div>
      {isOpened && (
        <div
          className={classNames(classes.scrollWrapper, scrollWrapperClass, {
            [classes.scrollWrapperNoPadding]: disableScroll,
          })}
        >
          {disableScroll ? (
            <div
              className={classNames(
                classes.content,
                classes.staticContent,
                contentClass
              )}
            >
              {children}
            </div>
          ) : (
            <Scrollbars verticalScrollClass={classes.scrollY}>
              <div className={classNames(classes.content, contentClass)}>
                {children}
              </div>
            </Scrollbars>
          )}
        </div>
      )}
    </Drawer>
  );
};

CustomDrawer.propTypes = {
  paperClass: PropTypes.string,
  scrollWrapperClass: PropTypes.string,
  contentClass: PropTypes.string,
  title: PropTypes.string.isRequired,
  isOpened: PropTypes.bool.isRequired,
  children: PropTypes.element.isRequired,
  onClose: PropTypes.func.isRequired,
  isWholeScreen: PropTypes.bool,
  isFullWidth: PropTypes.bool,
  isMiddleWidth: PropTypes.bool,
  isLarge: PropTypes.bool,
  isSmallWidth: PropTypes.bool,
  disableScroll: PropTypes.bool,
};

export default CustomDrawer;
